import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORT_EMAIL } from '../constants/support';
import { legalPageUrl } from '../constants/legal';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import LanguageSelector from '../components/LanguageSelector';
import { FileText, AlertCircle, ArrowLeft, Mail } from 'lucide-react';
import './RegisterPage.css';

function RegisterPage() {
  const { t, i18n } = useTranslation('login');
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if redirected from login with unverified email
  const navState = location.state || {};

  // Form state
  const [step, setStep] = useState(navState.step || 'register');
  const [email, setEmail] = useState(navState.email || '');
  const [password, setPassword] = useState(navState.password || '');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    if (navState.step === 'verify' && navState.email) {
      // Auto-resend verification code for unverified user redirected from login
      api.register(navState.email, navState.password, undefined, i18n.language, true)
        .catch(() => setError(t('register.errors.generic')));
    }
    // Once, for the visit the login page redirected here: a later language switch must not send another code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.register(email, password, name || undefined, i18n.language, acceptedTerms);
      setStep('verify');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.error_code === 'disposable_email') {
        setError(t('register.errors.disposableEmail'));
      } else if (detail?.error_code === 'undeliverable_email') {
        setError(t('register.errors.undeliverableEmail'));
      } else if (err.response?.status === 409) {
        setError(t('register.errors.emailTaken'));
      } else if (err.response?.status === 422 && Array.isArray(detail)) {
        const messages = detail.map((e) => e.msg).join('. ');
        setError(messages);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError(t('register.errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await api.verify(email, code);
      // Auto-login with returned token
      api.setToken(data.access_token);
      localStorage.setItem('token', data.access_token);
      await authLogin(email, password);
      navigate('/');
    } catch (err) {
      if (err.response?.status === 409) {
        setError(t('register.errors.emailTaken'));
      } else {
        setError(t('register.errors.invalidCode'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResent(false);
    try {
      await api.register(email, password, name || undefined, i18n.language, true);
      setResent(true);
      setTimeout(() => setResent(false), 3000);
    } catch {
      setError(t('register.errors.generic'));
    }
  };

  const resetToRegister = () => {
    setStep('register');
    setCode('');
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-language-selector">
        <LanguageSelector />
      </div>
      <div className="login-container">
        <div className="login-header">
          <FileText size={40} className="login-icon" />
          {step === 'register' && (
            <>
              <h1>{t('register.title')}</h1>
              <p>{t('register.subtitle')}</p>
            </>
          )}
          {step === 'verify' && (
            <>
              <Mail size={40} className="login-icon" style={{ marginBottom: '0.5rem' }} />
              <h1>{t('register.verify.title')}</h1>
              <p>{t('register.verify.subtitle')} <strong>{email}</strong></p>
            </>
          )}
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {step === 'register' && (
          <>
            <form onSubmit={handleRegister} className="login-form">
              <div className="form-group">
                <label htmlFor="name">{t('register.form.name')}</label>
                <input
                  id="name"
                  type="text"
                  className="input"
                  placeholder={t('register.form.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">{t('register.form.email')}</label>
                <input
                  id="email"
                  type="email"
                  className="input"
                  placeholder={t('register.form.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">{t('register.form.password')}</label>
                <input
                  id="password"
                  type="password"
                  className="input"
                  placeholder={t('register.form.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  maxLength={128}
                  required
                />
              </div>

              <div className="form-group form-checkbox">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    required
                  />
                  <span dangerouslySetInnerHTML={{ __html: t('register.acceptTermsHtml', {
                    termsUrl: legalPageUrl('terms', i18n.language),
                    privacyUrl: legalPageUrl('privacy', i18n.language),
                    dpaUrl: legalPageUrl('dpa', i18n.language),
                  }) }} />
                </label>
              </div>

              <button
                type="submit"
                className="btn btn-primary submit-btn"
                disabled={loading || !acceptedTerms}
              >
                {loading ? (
                  <>
                    <div className="spinner" />
                    {t('register.form.submitting')}
                  </>
                ) : (
                  t('register.form.submit')
                )}
              </button>
            </form>

            <div className="register-disclaimer">
              {t('register.retentionNote', { days: 30 })}
            </div>

            <div className="register-corporate-hint">
              <p>{t('register.corporateHint')}</p>
              <div className="register-corporate-links">
                <a href="https://t.me/mrbuslov" target="_blank" rel="noopener noreferrer">Telegram</a>
                <a href={`mailto:${SUPPORT_EMAIL}`}>Email</a>
                <a href="https://wa.me/380983435878" target="_blank" rel="noopener noreferrer">WhatsApp</a>
              </div>
            </div>

            <div className="login-register-hint">
              {t('register.hasAccount')} <Link to="/login">{t('register.signIn')}</Link>
            </div>
          </>
        )}

        {step === 'verify' && (
          <>
            <form onSubmit={handleVerify} className="login-form">
              <div className="form-group">
                <input
                  id="code"
                  type="text"
                  className="input verify-code-input"
                  placeholder={t('register.verify.codePlaceholder')}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  autoFocus
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary submit-btn"
                disabled={loading || code.length !== 6}
              >
                {loading ? (
                  <>
                    <div className="spinner" />
                    {t('register.verify.verifying')}
                  </>
                ) : (
                  t('register.verify.submit')
                )}
              </button>
            </form>

            <div className="verify-actions">
              <button
                className="link-button"
                onClick={handleResend}
                disabled={resent}
              >
                {resent ? t('register.verify.resent') : t('register.verify.resend')}
              </button>
              <button className="link-button" onClick={resetToRegister}>
                <ArrowLeft size={14} />
                {t('register.verify.back')}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

export default RegisterPage;
